// The Zero-trust tab's words (impire-hq 02-DESIGN/03-console/01-the-console.md
// § Settings: the Zero-trust tab; 02-DESIGN/02-secrets/03-escrow.md § what the
// console shows): what the recovery shares are, who holds each, what Collect,
// New shares and Present do and when a person needs each, and one sentence
// for every answer a present can get. Pure, so the words are tested as the
// design writes them; the tab draws them.

import { PLATFORM_NAME } from "../../platform/names.ts";
import { when } from "../../ui/components.tsx";
import {
  EscrowCode,
  PresentOutcome,
  Slot,
  SlotState,
  type EscrowPresenter,
  type EscrowSlotStatus,
  type EscrowStatus,
  type ShareCheck,
} from "../../wire/escrow.ts";

/** The platform's name on every page (decision 0049): the shares' third holder. */
export const PLATFORM = PLATFORM_NAME;

/** Who is around the workspace: its name, and its parent's where it has one. */
export interface Holders {
  name: string;
  parent?: string;
}

/** explainer is the paragraph always shown at the top of the tab. */
export function explainer({ name, parent }: Holders): string {
  const second =
    parent !== undefined
      ? `${parent}'s, held by the parent workspace`
      : `${PLATFORM}'s offline share, kept in ${PLATFORM}'s safe`;
  return (
    `Everything ${name} stores is locked with keys that no single party can rebuild. ` +
    `If they are ever lost, two of three recovery shares rebuild them: ${name}'s, held by one of ${name}'s admins; ` +
    `${second}; and ${PLATFORM}'s. No one holder can do it alone, and every recovery is recorded. ` +
    `While ${name} runs normally, nobody needs a share.`
  );
}

/** presenterWords names a presenter or collector; another workspace's people by no name. */
export function presenterWords(p: EscrowPresenter | undefined, own: boolean): string {
  if (p === undefined) {
    return "someone";
  }
  if (p.operator === true) {
    return `${PLATFORM}'s operator`;
  }
  if (p.workspace !== undefined && p.workspace !== "") {
    return `an admin of ${p.workspace}`;
  }
  if (!own) {
    return "an admin";
  }
  return p.principal?.display ?? p.principal?.subject ?? "an admin";
}

/** slotName is a share's name: *Acme's share*, *Northwind's share*, *Impire's share*. */
export function slotName(slot: string, h: Holders, state?: string): string {
  switch (slot) {
    case Slot.admin:
      return `${h.name}'s share`;
    case Slot.parent:
      if (state === SlotState.offline || h.parent === undefined) {
        return `${PLATFORM}'s offline share`;
      }
      return `${h.parent}'s share`;
    default:
      return `${PLATFORM}'s share`;
  }
}

/** slotWords says who holds a share and in what state. */
export function slotWords(s: EscrowSlotStatus, h: Holders): string {
  const on = s.collected !== undefined ? ` on ${when(s.collected)}` : "";
  switch (s.slot) {
    case Slot.admin:
      if (s.state === SlotState.waiting) {
        return "Waiting for an admin to collect it";
      }
      return `Collected by ${presenterWords(s.collected_by, true)}${on}`;
    case Slot.parent:
      if (s.state === SlotState.offline) {
        return `Sealed, opened only from ${PLATFORM}'s safe`;
      }
      if (s.state === SlotState.collected) {
        return `Collected by an admin of ${h.parent ?? "the parent workspace"}${on}`;
      }
      return `Held by ${h.parent ?? "the parent workspace"}`;
    default:
      return `Held by ${PLATFORM}`;
  }
}

/** SLOT_ORDER is the table's order: the workspace's own share, the parent's, the platform's. */
export const SLOT_ORDER: Readonly<Record<string, number>> = {
  [Slot.admin]: 0,
  [Slot.parent]: 1,
  [Slot.platform]: 2,
};

/** causeWords says why the current set was made, after *issued on <date>*. */
export function causeWords(cause: string | undefined, name: string): string | undefined {
  switch (cause) {
    case "creation":
      return `when ${name} was created`;
    case "resplit":
      return "when an admin issued a new set";
    case "parent-changed":
      return `when ${name}'s parent workspace changed`;
    case "key-rotated":
      return `when one of ${name}'s keys was renewed`;
    case "recovered":
      return "after a recovery";
    case "sweep":
      return `when ${PLATFORM} renewed them`;
    default:
      return undefined;
  }
}

/** setLine is the set's line: when it was issued and why. */
export function setLine(st: EscrowStatus, name: string): string {
  const why = causeWords(st.cause, name);
  return `Recovery shares issued on ${when(st.made)}${why !== undefined ? `, ${why}` : ""}.`;
}

/** SET_ID_HINT sits beside the set's id. */
export const SET_ID_HINT = "Each share carries this id; a share with another id no longer works.";

/**
 * replacedLine says a newer set replaced an earlier one, while the
 * workspace's own share of the new set waits: a share collected from the
 * earlier set no longer works.
 */
export function replacedLine(st: EscrowStatus, name: string): string | undefined {
  const own = st.slots.find((s) => s.slot === Slot.admin);
  if (st.cause === undefined || st.cause === "creation" || own?.state !== SlotState.waiting) {
    return undefined;
  }
  return `These replace an earlier set: a copy of ${name}'s share collected before ${when(st.made)} no longer works.`;
}

export const COLLECT_HINT = `Collect it once and keep it outside ${PLATFORM} — a password manager, or printed and locked away. It is shown only once.`;

/** collectedLine sits under the one-time value when Collect shows it in the tab's place. */
export function collectedLine(name: string): string {
  return (
    `This is the only time ${name}'s share is shown. Whoever holds it and one other share can rebuild ` +
    `${name}'s keys — keep it where only you can reach it.`
  );
}

export function issueAnewLine(name: string): string {
  return (
    "Make a new set of three recovery shares to replace the current ones. Do this when a share may have been seen by someone who should not have it, " +
    `when the colleague who holds ${name}'s share leaves, or when you want to hold it yourself.`
  );
}

export const ISSUE_ANEW_WHILE_OPEN = "New shares can be issued once the recovery ends.";

/** NEW_SHARES is the action's name; its confirmation is titled and confirmed in the same words (issue 181). */
export const NEW_SHARES = "New shares";
export const NEW_SHARES_TITLE = "Issue a new set of recovery shares?";
export const NEW_SHARES_VERB = "Issue new shares";

/** issueAnewConsequence is New shares' confirmation, naming whose shares stop working. */
export function issueAnewConsequence(st: EscrowStatus, h: Holders): string {
  const own = st.slots.find((s) => s.slot === Slot.admin);
  const parent = st.slots.find((s) => s.slot === Slot.parent);
  const held: string[] = [];
  if (own?.state === SlotState.collected) {
    held.push(`${presenterWords(own.collected_by, true)}'s copy of ${h.name}'s share`);
  }
  if (parent?.state === SlotState.collected && h.parent !== undefined) {
    held.push(`${h.parent}'s`);
  }
  const first =
    held.length > 0
      ? `The current shares stop working the moment the new set exists — ${held.join(" and ")} included.`
      : "The current shares stop working the moment the new set exists.";
  const ask =
    h.parent !== undefined && parent?.state !== SlotState.offline
      ? `, and ${h.parent} is asked to collect theirs again`
      : "";
  return `${first} A new ${h.name} share will wait here to be collected${ask}.`;
}

export function presentLine(name: string): string {
  return (
    `Hand in ${name}'s share to rebuild ${name}'s keys. You need this only when ${PLATFORM} or your parent ` +
    `workspace tells you ${name}'s keys were lost — after a restore, or a move to another installation. ` +
    `Never while ${name} runs normally.`
  );
}

export function presentConsequence(name: string): string {
  return (
    `This starts a recovery of ${name}'s keys. It is recorded, and it completes only when a second holder ` +
    "hands in theirs before it ends."
  );
}

/** checkAnswer is the sentence for a share the console refuses before sending, or nothing for a good one. */
export function checkAnswer(check: ShareCheck, slug: string, name: string): string | undefined {
  if (!check.ok) {
    return "This is not a recovery share; check for a missing or mistyped character.";
  }
  if (check.slug !== slug) {
    return `That share is ${check.slug}'s, not ${name}'s.`;
  }
  return undefined;
}

/** refusalAnswer is the sentence for a present the service refused, or nothing for a code without one. */
export function refusalAnswer(err: unknown): string | undefined {
  const code = typeof err === "object" && err !== null && "code" in err ? err.code : undefined;
  switch (code) {
    case EscrowCode.stale:
      return "This share belongs to an earlier set and no longer works.";
    case EscrowCode.mismatch:
      return "The two shares do not belong together. Check each against the set's id above and present again, or ask the third holder.";
    case EscrowCode.open:
      return ISSUE_ANEW_WHILE_OPEN;
    default:
      return undefined;
  }
}

/** outcomeAnswer is the sentence for a present the service took. */
export function outcomeAnswer(outcome: string, name: string): string {
  if (outcome === PresentOutcome.recovered) {
    return `${name}'s keys are rebuilt. A new set of shares was issued; collect ${name}'s below.`;
  }
  return `${name}'s share is in. The recovery completes when a second holder hands in theirs.`;
}

/** ceremonyTitle heads the card of an open recovery. */
export function ceremonyTitle(name: string): string {
  return `A recovery of ${name}'s keys is in progress`;
}

/**
 * The Zero-trust tab's forms, named in the history entry's state: on
 * Settings and on a child's page alike, the prefix tells the page the tab a
 * refreshed form stands in. The entry names the form, never the share.
 */
export const COLLECT_FORM = "zero-trust/collect";
export const PRESENT_FORM = "zero-trust/present";
export type ZeroTrustForm = typeof COLLECT_FORM | typeof PRESENT_FORM;

/**
 * shareStep is what a change of the page's form does to a collected share
 * the tab holds in memory: leaving the share's entry — *Done*, or Back —
 * drops it for good, so Forward finds nothing to show again. Any other
 * change keeps it (spec 061).
 */
export function shareStep<H>(
  from: string | undefined,
  to: string | undefined,
  held: H | undefined,
): H | undefined {
  return from === COLLECT_FORM && to !== from ? undefined : held;
}

/**
 * collectPushes reports whether Collect opens its form as a new history
 * entry: not where the entry is already Collect's — a refresh onto a spent
 * one — so no two entries of that name stand in the history and Back from
 * the share always leaves it, as Cancel does.
 */
export function collectPushes(open: string | undefined): boolean {
  return open !== COLLECT_FORM;
}

/** shownShare is the share the tab draws: only while its entry is the form open. */
export function shownShare<H>(open: string | undefined, held: H | undefined): H | undefined {
  return open === COLLECT_FORM ? held : undefined;
}
